import { Col, DatePicker, Form, Input, Popover, Row, Spin, Table } from "antd";
import COLORS from "common/Colors";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Info, SearchOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";

dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

const MinePerformanceWrap = styled.div`
  .ant-input-affix-wrapper,
  .ant-input,
  .ant-btn {
    border-radius: 24px;
  }

  .ant-select .ant-select-selector {
    border-radius: 24px;
  }

  .ant-input::placeholder {
    color: #000;
  }

  .ant-input:hover::placeholder {
    color: "#2d53da";
  }
  .ant-input::placeholder,
  .ant-picker-input > input::placeholder {
    color: #9ca3af !important;
  }

  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }

  .ant-input,
  .ant-picker,
  .ant-select-selector,
  .ant-btn {
    border-radius: 24px !important;
  }

  .ant-btn {
    width: 100%;
  }
`;

const ActionWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  .ant-table-row:hover & {
    opacity: 1;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-table-container {
    border-radius: 12px;
  }

  .ant-table-content {
    overflow-x: auto;
  }

  table {
    width: 100%;
    min-width: 900px;
  }

  .ant-table-cell {
    padding: 16px;
    white-space: nowrap;
  }

  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }

  .reason-column {
    min-width: 280px;
    white-space: normal;
  }

  .non-white {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    .ant-table-cell {
      padding: 10px;
      font-size: 12px;
    }
  }
`;

const MinePerformance = ({ setKpiDataList, setIsViewKpi, Menulist }) => {
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const history = useHistory();
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);

  const [dataList, setDataList] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageTitleRef = useRef(null);
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(120);

  // Watch form values
  const searchName = Form.useWatch("name", form);
  const filterDate = Form.useWatch("date", form);
  const filterType = Form.useWatch("type", form);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance/mineappraisal/${
          // "nexuspro@gmail.com"
          userInfo?.userid
        }`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setDataList(Array.isArray(data) ? data : []);
        setLoading(false);
      } else {
        throw new Error(`Failed to fetch data: ${code}`);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  // Initialize filterList when dataList loads
  useEffect(() => {
    setFilterList(dataList);
  }, [dataList]);

  // Apply all filters together whenever any filter or data changes
  useEffect(() => {
    let filtered = [...(dataList || [])];

    // 1. Search by title
    if (searchName && searchName.trim() !== "") {
      filtered = filtered.filter((item) =>
        item?.title?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    // 2. Filter by attend start date (match exact date)
    if (filterDate) {
      filtered = filtered.filter((item) => {
        if (!item?.attendstartdate) return false;
        return dayjs(item.attendstartdate).isSame(dayjs(filterDate), "day");
      });
    }

    // 3. Filter by type:
    //    "1" = Attended (attendedon is filled)
    //    "2" = Not Attended (attendedon is empty/null)
    if (filterType && filterType.length > 0) {
      filtered = filtered.filter((item) => {
        const isAttended = !!item?.attendedon;
        if (filterType.includes("1") && filterType.includes("2")) {
          // Both selected — show all
          return true;
        }
        if (filterType.includes("1")) return isAttended;
        if (filterType.includes("2")) return !isAttended;
        return true;
      });
    }

    setFilterList(filtered);
  }, [searchName, filterDate, filterType, dataList]);

  // Fetch on mount only (date/type filters are applied client-side)
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!pageTitleRef.current) return;

    const baseOffset = 300;
    const minHeight = 120;

    const calculateScrollY = () => {
      const headerHeight = pageTitleRef.current?.offsetHeight || 0;
      const availableHeight = window.innerHeight - headerHeight - baseOffset;
      setScrollY(Math.max(minHeight, availableHeight));
    };

    calculateScrollY();

    const resizeObserver = new ResizeObserver(calculateScrollY);
    resizeObserver.observe(pageTitleRef.current);
    window.addEventListener("resize", calculateScrollY);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", calculateScrollY);
    };
  }, []);

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => text || "-",
    },
    {
      title: "KPI Issued by",
      dataIndex: "createdat",
      key: "createdat",
      render: (text, record) => {
        return text ? (
          <>
            {userList?.find((item) => item?.userid === record?.appraiseruserid)
              ?.fullname || "-"}
            <br />
            {dayjs(text).format("Do MMM YYYY")}
          </>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Attend Appraisal from",
      dataIndex: "attendstartdate",
      key: "attendstartdate",
      render: (text) => {
        if (!text) return "-";
        const today = dayjs();
        const date = dayjs(text);
        const isSame = date.isSame(today, "day");
        return isSame ? (
          <div className="text-danger">{date.format("Do MMM YYYY")}</div>
        ) : (
          date.format("Do MMM YYYY")
        );
      },
    },
    {
      title: "Attended on",
      dataIndex: "attendedon",
      key: "attendedon",
      render: (text) => (text ? dayjs(text).format("Do MMM YYYY") : "-"),
    },
    {
      key: "actions",
      width: 130,
      render: (text, record) => {
        console.log("record-->", record);

        const attendanceDate = dayjs(record?.attendstartdate);
        const today = dayjs();

        // Attendance is allowed from the appraisal date
        // until 7 days after the appraisal date
        const attendanceEndDate = attendanceDate.add(7, "day");

        const isWithinAttendanceWeek =
          (today.isSame(attendanceDate, "day") ||
            today.isAfter(attendanceDate, "day")) &&
          (today.isSame(attendanceEndDate, "day") ||
            today.isBefore(attendanceEndDate, "day"));

        const isBeforeAttendanceDate = today.isBefore(attendanceDate, "day");

        return (
          <>
            <ActionWrapper>
              <p
                onClick={(e) => {
                  e.stopPropagation();
                  setKpiDataList(record);
                  setIsViewKpi(true);
                }}
                style={{ cursor: "pointer" }}
              >
                View KPI
              </p>

              {isBeforeAttendanceDate && (
                <Popover
                  content={
                    <>
                      You can attend the appraisal from
                      <br />
                      the date specified here.
                    </>
                  }
                  arrow
                >
                  <Info
                    sx={{
                      color: "#2D53DA",
                      fontSize: "20px",
                      marginRight: "8px",
                      cursor: "pointer",
                    }}
                  />
                </Popover>
              )}
            </ActionWrapper>
            {!record?.attendedon && (
              <>
                {isWithinAttendanceWeek && (
                  <ActionWrapper>
                    <p
                      onClick={() =>
                        history.push("/attend", {
                          dataList: record,
                          isView: false,
                        })
                      }
                      style={{
                        cursor: "pointer",
                        color: "#2d53da",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ArrowOutwardIcon fontSize="small" />
                      Attend
                    </p>
                  </ActionWrapper>
                )}
              </>
            )}
          </>
        );
      },
    },
  ];

  return (
    <MinePerformanceWrap>
      <div className="page-title" ref={pageTitleRef}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12} lg={8} xl={4} ref={containerRef}>
              <Form.Item name="name">
                <Input
                  placeholder="Search"
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4} ref={containerRef}>
              <Form.Item name="date">
                <DatePicker
                  placeholder="Date"
                  format="DD-MM-YYYY"
                  className="w-100"
                  allowClear
                  getPopupContainer={() => containerRef.current}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4} ref={containerRef}>
              <Form.Item name="type">
                <DropdownCheckBox
                  name="type"
                  label="Type"
                  options={[
                    { label: "Not Attended", value: "2" },
                    { label: "Attended", value: "1" },
                  ]}
                  className="input_field"
                  customStyle
                  form={form}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>

      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={filterList}
          pagination={false}
          scroll={{ y: scrollY }}
          style={{ minHeight: scrollY + 50 }}
          rowKey={(record) =>
            `${record?.id || record?.attendstartdate}-${
              record?.appraiseeuserid
            }`
          }
          rowClassName={(_, index) => (index % 2 ? "non-white" : "")}
          onRow={(record) => ({
            onClick: (e) => {
              if (record?.attendedon) {
                history.push("/attend", {
                  dataList: record,
                  isview: true,
                });
              } else {
                e.stopPropagation();
                setKpiDataList(record);
                setIsViewKpi(true);
              }
            },
          })}
        />
      </Spin>
    </MinePerformanceWrap>
  );
};

export default MinePerformance;
